import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Button } from 'new/elements/Button';
import { TextField } from 'new/elements/TextField';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { useUpdateExtMcpServerMutation } from '../../../state/aiApi';
import type { ApiExtMcpServer } from '../../../state/aiApiTypes';
import styles from './GatewaySection.module.scss';

interface EditExtMcpServerDialogProps {
  open: boolean;
  toolsetId: number;
  server: ApiExtMcpServer;
  onClose: () => void;
}

const EditExtMcpServerDialog: React.FC<EditExtMcpServerDialogProps> = ({
  open,
  toolsetId,
  server,
  onClose,
}) => {
  const { showSuccess } = useSnackbar();
  const [updateServer, { isLoading }] = useUpdateExtMcpServerMutation();

  const [serverName, setServerName] = useState(server.server_name);
  const [serverDescription, setServerDescription] = useState(server.server_description ?? '');
  const [serverUrl, setServerUrl] = useState(server.server_url ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!serverName.trim()) return;
    setSubmitError(null);
    try {
      await updateServer({
        toolsetId,
        serverId: server.id,
        data: {
          server_name: serverName.trim(),
          server_description: serverDescription.trim() || undefined,
          server_url: serverUrl.trim() || undefined,
        },
      }).unwrap();
      showSuccess('Server updated');
      onClose();
    } catch (err: any) {
      setSubmitError(err?.data?.message || 'Failed to update server');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        pt={2.5}
        pb={1}
      >
        <Typography variant="h6">Edit MCP Server</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <TextField
            label="Server Name"
            value={serverName}
            onChange={(e) => setServerName(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Description"
            value={serverDescription}
            onChange={(e) => setServerDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />

          <TextField
            label="Server URL"
            value={serverUrl}
            onChange={(e) => setServerUrl(e.target.value)}
            placeholder="https://..."
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!serverName.trim() || isLoading}
          startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditExtMcpServerDialog;
